/**
 * TBC USP Bar — mobile Swiper (uses theme global Swiper).
 * Safe init retry, reduced-motion, breakpoint destroy, theme editor lifecycle.
 * Mobile columns = slidesPerView (1–4) for every item-count combination.
 */
(function () {
  const MOBILE_MQ = '(max-width: 749px)';
  const REDUCE_MQ = '(prefers-reduced-motion: reduce)';
  const MAX_SWIPER_WAIT_FRAMES = 60;

  class TbcUspBarSlider extends HTMLElement {
    connectedCallback() {
      this.readSettings();
      this.mql = this.mql || window.matchMedia(MOBILE_MQ);
      this.motionMql = this.motionMql || window.matchMedia(REDUCE_MQ);

      this.onBreakpoint = this.onBreakpoint.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);

      this.destroySwiper();
      this.unbindMedia();

      if (!this.enabled) return;

      this.whenSwiperReady(() => {
        this.onBreakpoint();
        this.bindMedia();
      });
    }

    disconnectedCallback() {
      this.unbindMedia();
      this.destroySwiper();
    }

    readSettings() {
      this._readyAttempts = 0;
      this.enabled = this.dataset.slider === 'true';
      this.autoplayDelay = Number(this.dataset.autoplayDelay) || 4000;
      this.loop = this.dataset.loop === 'true';
      this.autoplayEnabled = this.dataset.autoplay !== 'false';
      this.columnsMobile = Math.min(4, Math.max(1, parseInt(this.dataset.columnsMobile, 10) || 3));
    }

    bindMedia() {
      if (this._mediaBound) return;
      this._mediaBound = true;
      this.mql.addEventListener('change', this.onBreakpoint);
      this.motionMql.addEventListener('change', this.onMotionChange);
    }

    unbindMedia() {
      if (!this._mediaBound) return;
      this._mediaBound = false;
      this.mql.removeEventListener('change', this.onBreakpoint);
      this.motionMql.removeEventListener('change', this.onMotionChange);
    }

    whenSwiperReady(callback) {
      if (typeof window.Swiper !== 'undefined') {
        callback();
        return;
      }
      if (this._readyAttempts >= MAX_SWIPER_WAIT_FRAMES) return;
      this._readyAttempts += 1;
      requestAnimationFrame(() => this.whenSwiperReady(callback));
    }

    onBreakpoint() {
      if (!this.enabled || !this.mql.matches) {
        this.destroySwiper();
        return;
      }
      this.createSwiper();
    }

    onMotionChange() {
      this.destroySwiper();
      this.onBreakpoint();
    }

    createSwiper() {
      this.destroySwiper();
      if (typeof window.Swiper === 'undefined') return;

      const el = this.querySelector('.tbc-section-usp-bar__swiper');
      if (!el) return;

      const slides = el.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)');
      const slideCount = slides.length;
      const perView = Math.min(this.columnsMobile, Math.max(1, slideCount));
      const canSlide = slideCount > perView;

      this.classList.toggle('is-single-view', perView === 1);

      if (!canSlide) {
        this.classList.add('is-swiper-static');
        return;
      }

      const reduceMotion = this.motionMql.matches;
      const canLoop = this.loop && !reduceMotion && slideCount >= perView * 2;
      const useAutoplay = this.autoplayEnabled && !reduceMotion;

      this.swiper = new window.Swiper(el, {
        slidesPerView: perView,
        slidesPerGroup: 1,
        spaceBetween: 0,
        speed: reduceMotion ? 0 : 450,
        loop: canLoop,
        rewind: !canLoop,
        autoplay: useAutoplay
          ? {
              delay: this.autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        autoHeight: perView === 1,
        a11y: { enabled: true },
        on: {
          init: () => {
            this.classList.add('is-swiper');
          },
        },
      });
    }

    destroySwiper() {
      this.classList.remove('is-swiper', 'is-swiper-static', 'is-single-view');
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    slideToBlock(blockId) {
      if (!this.swiper || !blockId) return;
      const slide = this.querySelector(`.swiper-slide[data-block-id="${blockId}"]`);
      if (!slide) return;
      const index = Number(slide.getAttribute('data-swiper-slide-index'));
      const fallbackIndex = Array.from(
        this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)')
      ).indexOf(slide);

      const target = Number.isFinite(index) ? index : fallbackIndex;
      if (target < 0) return;

      if (this.swiper.params.loop && typeof this.swiper.slideToLoop === 'function') {
        this.swiper.slideToLoop(target);
      } else {
        this.swiper.slideTo(target);
      }
    }
  }

  if (!customElements.get('tbc-usp-bar-slider')) {
    customElements.define('tbc-usp-bar-slider', TbcUspBarSlider);
  }

  document.addEventListener('shopify:section:load', (event) => {
    const slider = event.target.querySelector?.('tbc-usp-bar-slider');
    if (!slider) return;
    slider.disconnectedCallback?.();
    slider.connectedCallback?.();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector?.('tbc-usp-bar-slider')?.destroySwiper?.();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    const slider = block.closest?.('tbc-usp-bar-slider');
    const blockId = block.dataset?.blockId || event.detail?.blockId;
    slider?.slideToBlock?.(blockId);
    if (slider?.swiper?.autoplay) slider.swiper.autoplay.stop();
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const slider = event.target.closest?.('tbc-usp-bar-slider');
    if (slider?.swiper?.autoplay && slider.autoplayEnabled && !slider.motionMql?.matches) {
      slider.swiper.autoplay.start();
    }
  });
})();
